import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { meetingsApi } from '../api';

const statusInfo = {
  aufgenommen: { label: '📁 Aufgenommen', color: '#64748b' },
  hochgeladen: { label: '⬆️ Hochgeladen', color: '#0ea5e9' },
  transkribiert_laufend: { label: '⏳ Whisper läuft...', color: '#f59e0b' },
  transkribiert: { label: '📝 Transkribiert', color: '#8b5cf6' },
  analysiert: { label: '🤖 KI analysiert...', color: '#6366f1' },
  fertig: { label: '✅ In Paperless', color: '#10b981' },
  fehler: { label: '❌ Fehler', color: '#ef4444' },
};

export default function MeetingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [analyse, setAnalyse] = useState(null);
  const [aktiv, setAktiv] = useState(true);

  const load = async () => {
    try {
      const res = await meetingsApi.get(id);
      setMeeting(res.data);
      if (res.data.zusammenfassung) {
        try { setAnalyse(JSON.parse(res.data.zusammenfassung)); } catch {}
      }
      if (['fertig', 'fehler', 'analysiert'].includes(res.data.status)) setAktiv(false);
    } catch {}
  };

  useEffect(() => {
    load();
    const interval = setInterval(() => { if (aktiv) load(); }, 3000);
    return () => clearInterval(interval);
  }, [id, aktiv]);

  if (!meeting) return <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>⏳ Laden...</div>;

  const info = statusInfo[meeting.status] || statusInfo.aufgenommen;
  const datum = new Date(meeting.datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  const laufend = ['transkribiert_laufend', 'analysiert', 'hochgeladen'].includes(meeting.status);

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: 16 }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: 14 }}>← Zurück</button>
          <h1 style={{ flex: 1, color: '#f1f5f9', fontSize: 17, fontWeight: 700 }}>{meeting.titel}</h1>
        </div>

        {/* Status */}
        <div style={{ background: '#1e293b', borderRadius: 12, padding: 14, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: '#64748b', fontSize: 12 }}>{datum}</div>
            {meeting.teilnehmer && <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>👥 {meeting.teilnehmer}</div>}
            {meeting.ort && <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>📍 {meeting.ort}</div>}
            {meeting.dauer_sekunden && <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 2 }}>⏱️ {Math.round(meeting.dauer_sekunden / 60)} Minuten</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ background: info.color + '22', color: info.color, padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>
              {info.label}
            </span>
            {laufend && <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>Wird verarbeitet...</div>}
          </div>
        </div>

        {/* Ladeindikator */}
        {laufend && (
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 14, textAlign: 'center' }}>
            <div style={{ color: '#6366f1', fontSize: 14, marginBottom: 8 }}>
              {meeting.status === 'transkribiert_laufend' ? '🎙️ Whisper transkribiert...' : '🤖 Mistral analysiert...'}
            </div>
            <div style={{ background: '#334155', borderRadius: 4, height: 6, overflow: 'hidden' }}>
              <div style={{ background: '#6366f1', height: 6, width: '60%', animation: 'none', borderRadius: 4 }} />
            </div>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>Seite aktualisiert automatisch alle 3 Sekunden</div>
          </div>
        )}

        {/* KI Analyse */}
        {analyse && (
          <>
            {analyse.zusammenfassung && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>💬 Zusammenfassung</h3>
                <p style={{ color: '#f1f5f9', fontSize: 14, lineHeight: 1.6 }}>{analyse.zusammenfassung}</p>
              </div>
            )}

            {analyse.aktionspunkte?.length > 0 && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>✅ Aktionspunkte</h3>
                {analyse.aktionspunkte.map((a, i) => (
                  <div key={i} style={{ background: '#0f172a', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                    <div style={{ color: '#f1f5f9', fontSize: 14, fontWeight: 500 }}>{i+1}. {a.aufgabe}</div>
                    {a.verantwortlich && <div style={{ color: '#6366f1', fontSize: 12, marginTop: 4 }}>👤 {a.verantwortlich}</div>}
                    {a.frist && <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 2 }}>📅 bis {a.frist}</div>}
                  </div>
                ))}
              </div>
            )}

            {analyse.hauptthemen?.length > 0 && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>🎯 Hauptthemen</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {analyse.hauptthemen.map((t, i) => (
                    <span key={i} style={{ background: '#6366f122', color: '#818cf8', padding: '4px 10px', borderRadius: 12, fontSize: 13 }}>{t}</span>
                  ))}
                </div>
              </div>
            )}

            {analyse.entscheidungen?.length > 0 && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>⚖️ Entscheidungen</h3>
                {analyse.entscheidungen.map((e, i) => (
                  <div key={i} style={{ color: '#f1f5f9', fontSize: 14, padding: '6px 0', borderBottom: i < analyse.entscheidungen.length-1 ? '1px solid #334155' : 'none' }}>
                    {i+1}. {e}
                  </div>
                ))}
              </div>
            )}

            {analyse.naechste_schritte && (
              <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>🚀 Nächste Schritte</h3>
                <p style={{ color: '#f1f5f9', fontSize: 14, lineHeight: 1.6 }}>{analyse.naechste_schritte}</p>
              </div>
            )}
          </>
        )}

        {/* Transkript */}
        {meeting.transkript && (
          <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <h3 style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>📝 Transkript</h3>
            <p style={{ color: '#cbd5e1', fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto' }}>
              {meeting.transkript}
            </p>
          </div>
        )}

        {/* Paperless Status */}
        {meeting.status === 'fertig' && (
          <div style={{ background: '#10b98122', border: '1px solid #10b98144', borderRadius: 12, padding: 14, marginBottom: 14, textAlign: 'center' }}>
            <div style={{ color: '#34d399', fontSize: 14, fontWeight: 600 }}>✅ Automatisch in Paperless importiert!</div>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>Das Meeting-Protokoll ist als PDF in Paperless verfügbar.</div>
          </div>
        )}

        {/* Löschen */}
        <button onClick={async () => {
          if (!window.confirm('Meeting löschen?')) return;
          await meetingsApi.delete(id);
          navigate('/');
        }} style={{ background: '#ef444422', color: '#f87171', border: '1px solid #ef444444', padding: '10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, width: '100%', marginTop: 8 }}>
          🗑️ Meeting löschen
        </button>
      </div>
    </div>
  );
}
